import API_BASE from './config';

// Local storage keys and persistence removed — always use backend-first behavior.

function ensureRaces() {
    // Local storage removed; return empty array as a non-persistent fallback.
    return [];
}

export function getRaces() {
    // try backend first
    return fetch(`${API_BASE}/races`, { headers: { 'Accept': 'application/json' } })
        .then((res) => {
            if (!res.ok) throw new Error('Network response not ok');
            const ct = res.headers.get('content-type') || '';
            if (!ct.includes('application/json')) throw new Error('Non-JSON response');
            return res.json();
        })
        .then((rows) => rows)
        .catch((err) => {
            console.error('raceApi.getRaces error', err);
            return Promise.resolve([]);
        });
}

export function syncRaces(season = 'current') {
    return fetch(`${API_BASE}/races/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ season }),
    })
        .then((res) => {
            if (!res.ok) throw new Error('Failed to sync races');
            return res.json();
        })
        .catch((err) => {
            console.error('raceApi.syncRaces error', err);
            return Promise.reject(err);
        });
}

export function addRace(name, localDate, localTime) {
    try {
        // Parse local date/time into a JS Date using local timezone
        // localDate expected 'YYYY-MM-DD', localTime expected 'HH:MM' or 'HH:MM:SS'
        const [y, m, d] = (localDate || '').split('-').map((v) => Number(v));
        const timeParts = (localTime || '').split(':').map((v) => Number(v));
        const hh = timeParts[0] || 0;
        const mm = timeParts[1] || 0;
        const ss = timeParts[2] || 0;
        const dt = new Date(y, (m || 1) - 1, d || 1, hh, mm, ss);
        const iso = isNaN(dt.getTime()) ? null : dt.toISOString();
        return fetch(`${API_BASE}/races`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ name, start_time: iso }),
        })
            .then((res) => {
                if (!res.ok) {
                    throw new Error('Failed to create race');
                }
                return res.json();
            })
            .then((row) => Promise.resolve(row))
            .catch((err) => {
                console.error('raceApi.addRace error', err);
                return Promise.reject(err);
            });
    } catch (err) {
        console.error('raceApi.addRace parse error', err);
        return Promise.reject(err);
    }
}

export function getRace(raceId) {
    return fetch(`${API_BASE}/races/${raceId}`, { headers: { 'Accept': 'application/json' } })
        .then((res) => {
            if (!res.ok) throw new Error('Network response not ok');
            const ct = res.headers.get('content-type') || '';
            if (!ct.includes('application/json')) throw new Error('Non-JSON response');
            return res.json();
        })
        .then((r) => r)
        .catch((err) => {
            console.error('raceApi.getRace error', err);
            return Promise.resolve(null);
        });
}

export function getRaceWeekendSummary(raceId) {
    if (!raceId) return Promise.resolve(null);
    return fetch(`${API_BASE}/races/${encodeURIComponent(raceId)}/weekend-summary`, { headers: { 'Accept': 'application/json' } })
        .then((res) => {
            if (!res.ok) throw new Error('Failed to fetch weekend summary');
            const ct = res.headers.get('content-type') || '';
            if (!ct.includes('application/json')) throw new Error('Non-JSON response');
            return res.json();
        })
        .catch((err) => {
            console.error('raceApi.getRaceWeekendSummary error', err);
            return Promise.resolve(null);
        });
}

export function setRaceLock(raceId, locked) {
    // Set the season-state to the specified race and lock state.
    // The season_state table controls the lock for whichever race is the current_race_id.
    const payload = { current_race_id: Number(raceId), picks_locked: locked ? 1 : 0 };
    return fetch(`${API_BASE}/season-state`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
    })
        .then((res) => {
            if (!res.ok) throw new Error('Failed to update season state');
            // reflect change locally when possible
            return Promise.resolve({ id: raceId, locked: !!locked });
        })
        .catch((err) => {
            console.error('raceApi.setRaceLock error', err);
            return Promise.reject(err);
        });
}

export function isRaceLocked(raceId) {
    return fetch(`${API_BASE}/season-state`, { headers: { 'Accept': 'application/json' } })
        .then((res) => {
            if (!res.ok) throw new Error('Network response not ok');
            const ct = res.headers.get('content-type') || '';
            if (!ct.includes('application/json')) throw new Error('Non-JSON response');
            return res.json();
        })
        .then((state) => {
            if (!state) return false;
            return Boolean(state.picks_locked && String(state.current_race_id) === String(raceId));
        })
        .catch((err) => {
            console.error('raceApi.isRaceLocked error', err);
            return Promise.resolve(false);
        });
}

export function getCurrentRace() {
    return fetch(`${API_BASE}/season-state`, { headers: { 'Accept': 'application/json' } })
        .then((res) => {
            if (!res.ok) throw new Error('Network response not ok');
            const ct = res.headers.get('content-type') || '';
            if (!ct.includes('application/json')) throw new Error('Non-JSON response');
            return res.json();
        })
        .then((state) => (state && (state.current_race_id != null ? String(state.current_race_id) : null)) || null)
        .catch((err) => {
            console.error('raceApi.getCurrentRace error', err);
            return Promise.resolve(null);
        });
}

export function setCurrentRace(raceId) {
    // Update season-state current_race_id, preserve picks_locked when possible
    return fetch(`${API_BASE}/season-state`, { headers: { 'Accept': 'application/json' } })
        .then((res) => {
            if (!res.ok) throw new Error('Network response not ok');
            const ct = res.headers.get('content-type') || '';
            if (!ct.includes('application/json')) throw new Error('Non-JSON response');
            return res.json();
        })
        .then((state) => {
            const currentRaceId = state && state.current_race_id != null ? Number(state.current_race_id) : null;
            const nextRaceId = Number(raceId);
            const picks_locked = currentRaceId !== null && currentRaceId !== nextRaceId
                ? 0
                : (state && typeof state.picks_locked !== 'undefined' ? state.picks_locked : 0);
            return fetch(`${API_BASE}/season-state`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ current_race_id: nextRaceId, picks_locked }),
            });
        })
        .then((res) => {
            if (!res.ok) throw new Error('Failed to update season state');
            return Promise.resolve(raceId);
        })
        .catch((err) => {
            console.error('raceApi.setCurrentRace error', err);
            return Promise.reject(err);
        });
}

export function setRaceLockTime(raceId, lockTime) {
    return fetch(`${API_BASE}/races/${encodeURIComponent(raceId)}/lock-time`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ pick_lock_time: lockTime }),
    })
        .then((res) => {
            if (!res.ok) throw new Error('Failed to update lock time');
            return res.json();
        })
        .catch((err) => {
            console.error('raceApi.setRaceLockTime error', err);
            return Promise.reject(err);
        });
}

// Test data toggles removed — always use backend-first behavior.
export function enableTestData(/* enable = true */) {
    return Promise.resolve(false);
}

export function isTestDataEnabled() {
    return Promise.resolve(false);
}
